import { actionChannel, all, call, delay, put, select, take, takeEvery } from 'redux-saga/effects';
import { Channel } from 'redux-saga';
import { closeTimerPill } from '@utils/calendar/markUsedCelebration';
import { followBlocks } from '@utils/calendar/floatToNow';
import { Dispatch } from 'redux';

import * as actions from './actions';
import { getDropped, getEntry, getSeq, getStep } from './selectors';
import { UNDO_STEPS } from './reducers';
import { PlanningUndo, TaskUndoReceipt, UndoEntry, UndoneTask, UndoStep } from './types';
import { stillAsLeft } from './guards';
import { apiCall } from '../common/operations';
import { applyTogether, clearState } from '../common/actions';
import { semesterSelectors } from '../semester';
import {
  clearState as clearCalendar,
  getPlannedTasks,
  markAsUsed,
  markBlocksUsed,
  planTasks,
  resizePlannedTask,
  updatePlannedTask
} from '../calendar/actions';
import { getPlannedEvent } from '../calendar/selectors';
import { deleteEvent, getEventsList, updateSemesterHolidays } from '../events/actions';
import { closeManageTaskDialog, deleteTasks, getManageTask } from '../task/actions';
import { setAwakeTimes, updateTerm } from '../terms/actions';
import { moveTaskInList } from '../workload/actions';
import { getActiveTimer } from '../timer/selectors';
import { timerActions } from '../timer';
import { plannerApi, timerApi } from '../../api';
import { ApplyOption, CalendarEvent } from 'shovel-lib/types';
import { applyUnplanResult } from '../calendar/operations';
import { SourceEvent } from '../events/types';
import { RootState } from '@state/types';
import { undoApi } from '@api/undoApi';
import { getErrorMessage } from '@api/network';
import { MarkUsedBlock, MarkUsedResponse, plannerApi as webPlannerApi } from '@api/plannerApi';
import { writesSettled } from '../../utils/serverChanges';
import { bottomToast, closeBottomToast } from '@components/common/snackbarNotifications/Notifications';
import { isMobile, isMobileApp, isTouchScreen, operatingSystem, OS } from '@utils/screenUtils';
import t from '../../i18n/t';

// Long enough to notice a slip and reach for the button; Cmd+Z keeps working after the toast is gone.
const UNDO_TOAST_MS = 8000;

// How long Cmd+Z waits for a change still on its way before taking the newest step anyway.
const WRITES_WAIT_MS = 4000;

// Shown next to Undo so students learn it; phones and tablets have no shortcut to learn.
const SHORTCUT = isMobile || isMobileApp || isTouchScreen ? undefined : operatingSystem === OS.MAC ? '⌘Z' : 'Ctrl Z';

const entryIcon = (entry: UndoEntry) => {
  if (entry.kind === 'timerReset' || entry.kind === 'sessionCleared') return 'restart_alt';
  if (entry.kind === 'timerStartRearranged' || entry.kind === 'sessionsBumped') return 'event_repeat';
  if (entry.kind === 'createdTasks' || entry.kind === 'createdEvent') return 'add_circle_outline';
  if (entry.kind === 'sessionMoved' || entry.kind === 'eventEdited') return 'event_repeat';
  if (entry.kind === 'sessionUsed') return 'check_circle_outline';
  if (entry.kind === 'listOrder') return 'swap_vert';
  if (entry.kind === 'planned') return entry.plannedSessionIds.length > 0 ? 'event_available' : 'event_busy';
  return entry.kind === 'tasks' && entry.receipt.action === 'COMPLETE' ? 'check_circle_outline' : 'delete_outline';
};

// Each entry's toast, so the change being undone takes its Undo button with it.
const closeToast = new WeakMap<UndoEntry, () => void>();
// Each change's step in the history, to put back where it was when its Undo can't reach the server.
const stepOf = new WeakMap<UndoEntry, UndoStep>();
// The term each change was made in: an Undo after switching terms has nothing to act on.
const semesterOf = new WeakMap<UndoEntry, number>();
// Changes being undone or undone already. A second Undo of the same change (a click on the fading toast after Cmd+Z)
// is dropped rather than racing the first.
const taken = new WeakSet<UndoEntry>();
// The newest step's number as the running Undo began (they run one at a time): moved on means another change came in.
let since = 0;
// The last thing that happened was an Undo, so Cmd+Z with nothing left says so rather than nothing.
let justUndone = false;

const offerUndo = (dispatch: Dispatch, entry: UndoEntry, message: string) => {
  // The button carries its own entry, so it undoes this change even after Cmd+Z has moved on.
  const close = bottomToast({
    message,
    icon: entryIcon(entry),
    action: { label: t.UNDO, onClick: () => dispatch(actions.undo.request(entry)) },
    shortcut: SHORTCUT,
    duration: UNDO_TOAST_MS
  });
  closeToast.set(entry, close);
};

export function* showUndoToast(dispatch: Dispatch, { payload }: ReturnType<typeof actions.record>) {
  const { entry, message, silent, toast } = payload;
  justUndone = false;
  semesterOf.set(entry, yield select(semesterSelectors.getId));
  const step: UndoStep | undefined = yield select(getStep, entry);
  if (step) stepOf.set(entry, step);
  if (silent && !toast) return;
  offerUndo(dispatch, entry, toast ?? message);
}

// What an undo says when it's done, unless another change came in meanwhile: that one is the news now.
function* report(message: string, icon: string) {
  if ((yield select(getSeq)) !== since) return;
  bottomToast({ message, icon });
}

// Couldn't reach the server: the step goes back in its place, for Cmd+Z, the Undo button or the toast to try again.
function* retryLater(entry: UndoEntry, error: string) {
  yield put(actions.undo.failure(error));
  taken.delete(entry);
  const step = stepOf.get(entry);
  if (step) yield put(actions.reinsert(step));
}

function* offerAgain(dispatch: Dispatch, { payload: step }: ReturnType<typeof actions.reinsert>) {
  if ((yield select(getSeq)) !== since) return;
  offerUndo(dispatch, step.entry, t.UNDO_FAILED);
}

// Refused, naming what it was where that's known: only this step goes, the ones before it are still there.
const changedSinceMessage = (entry: UndoEntry) => {
  if (entry.kind === 'sessionMoved') {
    return entry.how === 'moved' ? t.UNDO_MOVE_CHANGED_SINCE(entry.title) : t.UNDO_EDIT_CHANGED_SINCE(entry.title);
  }
  if (entry.kind === 'eventEdited') return t.UNDO_EDIT_CHANGED_SINCE(entry.before.title);
  if (entry.kind === 'sessionUsed') return t.UNDO_EDIT_CHANGED_SINCE(entry.title);
  return t.UNDO_CHANGED_SINCE;
};

function* changedSince(entry: UndoEntry) {
  yield put(actions.undo.failure('changed since'));
  yield call(report, changedSinceMessage(entry), 'info');
}

function* clearTimerOn(isRunningOn: (timer: { masterTaskId?: number; plannedTaskId?: number }) => boolean) {
  const activeTimer = yield select(getActiveTimer);
  if (activeTimer && isRunningOn(activeTimer)) {
    yield put(timerActions.clearSession());
  }
}

function* undoTasks(entry: UndoEntry, semesterId: number, receipt: TaskUndoReceipt) {
  const { ok, ...response } = yield apiCall(undoApi.undoTasks, semesterId, receipt, entry.actionId);
  if (!ok) return yield call(retryLater, entry, getErrorMessage(response));
  const tasks: UndoneTask[] = response.data || [];
  // The server leaves alone a task restored from the trash or reopened since; nothing came back.
  if (tasks.length === 0) return yield call(changedSince, entry);
  const usedSessionId = (receipt.usedSession as { id?: number } | null)?.id;
  yield put(actions.tasksUndone({ action: receipt.action, tasks, usedSessionId }));
  yield put(actions.undo.success());
  // The study sessions came back as new blocks.
  yield put(getPlannedTasks.request());
  const { isOpen, task } = yield select((state: RootState) => state.task.manage);
  if (isOpen && task && tasks.some(undone => undone.taskId === task.taskId)) {
    yield put(getManageTask.request({ taskId: task.taskId }));
  }
  const message = receipt.action === 'DELETE' ? t.TASKS_RESTORED(tasks.length) : t.TASKS_BACK_TO_DO(tasks.length);
  yield call(report, message, 'undo');
}

// An event's delete or edit taken back: the event as it was, saved back under the same id.
function* undoEventChange(entry: UndoEntry, semesterId: number, before: SourceEvent, after: SourceEvent[]) {
  const edited = entry.kind === 'eventEdited';
  // A "This event" edit splits the occurrence off into an event of its own, and "This and following" or a "following
  // Tuesdays" delete the later or earlier dates into a new series; the restored one covers them again.
  const removeIds = after.map(saved => saved.id!).filter(id => id !== before.id);
  const { ok, ...response } = yield apiCall(undoApi.restoreEvents, semesterId, [before], removeIds, entry.actionId);
  if (!ok) return yield call(retryLater, entry, getErrorMessage(response));
  yield put(actions.eventsRestored({ restored: response.data, removeIds }));
  yield put(actions.undo.success());
  yield put(getEventsList.request({}));
  // An edit came without a toast and goes back without one: the event going back says it.
  if (!edited) yield call(report, t.EVENT_RESTORED, 'undo');
}

// A session's move or stretch taken back: a resize to where it was, with no toast, as it came.
function* undoSessionMoved(entry: Extract<UndoEntry, { kind: 'sessionMoved' }>, semesterId: number) {
  const { from, to, editedByStudent } = entry;
  const current: CalendarEvent | undefined = yield select(getPlannedEvent, to.id);
  const { ok, ...response } = yield apiCall(undoApi.moveSessionBack, semesterId, from, editedByStudent, entry.actionId);
  // Unplanned since.
  if (!ok && response.status === 404) return yield call(changedSince, entry);
  if (!ok) return yield call(retryLater, entry, getErrorMessage(response));
  // Sliding back from where it is, as a Bumpers drag's sessions do; not sideways within a day.
  followBlocks([to.id], { sideways: false });
  const oldEvent = current ?? { ...response.data, start: to.start, end: to.end };
  yield put(resizePlannedTask.success({ oldEvent, newEvent: response.data }));
  yield put(actions.undo.success());
}

/**
 * Time logged on a session taken off again, with the minutes and pages it had before; or a tick taken off put back on,
 * with the time it had. Quietly, as it came.
 */
function* undoSessionUsed(entry: Extract<UndoEntry, { kind: 'sessionUsed' }>, semesterId: number) {
  const { plannedTaskId, marked, before, after } = entry;
  // The untick took off the plan its tick had filled in (Petr, 2026-10-30): ticked again with no minutes, the server
  // fills the plan in again and remembers it did, so unticking once more takes it off as before.
  const filledIn = !marked && before.used > 0 && after.used === 0;
  const block: MarkUsedBlock = {
    plannedTaskId,
    used: !marked,
    // Unticked back to the minutes it had logged, often none; ticked again with the time it had.
    minutes: !filledIn && (marked || before.used > 0) ? before.used : undefined,
    // Only where the change set them (a reading's pages read): a block that had none gets none back.
    ...(before.pages !== after.pages ? { pages: before.pages ?? 0 } : {})
  };
  const { ok, ...response } = yield apiCall(undoApi.markSessionsUsed, semesterId, [block], entry.actionId);
  if (!ok) return yield call(retryLater, entry, getErrorMessage(response));
  const result: MarkUsedResponse | undefined = response.data;
  // Left alone: gone, ticked or unticked some other way since, or its timer running.
  if (!result?.plannedTasks.length) return yield call(changedSince, entry);
  yield put(markBlocksUsed.success(result));
  yield put(actions.undo.success());
}

// Undoing a creation deletes it for good, with the session planned along with it when it was made on the calendar.
// One bulk removal for the lot, rather than one per task: each of those would redraw and refetch on its own.
function* undoCreatedTasks(entry: UndoEntry, semesterId: number, taskIds: number[], planned: boolean) {
  const { ok, ...response } = yield apiCall(undoApi.deleteCreatedTasks, semesterId, taskIds, entry.actionId);
  if (!ok) return yield call(retryLater, entry, getErrorMessage(response));
  yield put(deleteTasks.success({ ids: taskIds, deleteForever: true }));
  yield put(actions.undo.success());
  if (planned) yield put(getPlannedTasks.request());
  const { isOpen, task } = yield select((state: RootState) => state.task.manage);
  if (isOpen && task && taskIds.includes(task.taskId)) {
    yield put(closeManageTaskDialog());
  }
  yield call(clearTimerOn, timer => taskIds.includes(timer.masterTaskId!));
  yield call(report, t.TASKS_REMOVED(taskIds.length), 'undo');
}

function* undoCreatedEvent(entry: UndoEntry, semesterId: number, event: SourceEvent) {
  // A repeating event goes as a whole series.
  const query = event.isRecurring ? { date: event.start, apply: ApplyOption.ALL } : undefined;
  const { ok, ...response } = yield apiCall(undoApi.deleteCreatedEvent, semesterId, event.id!, query, entry.actionId);
  // Already deleted some other way.
  if (!ok && response.status === 404) return yield call(changedSince, entry);
  if (!ok) return yield call(retryLater, entry, getErrorMessage(response));
  yield put(deleteEvent.success({ id: event.id!, data: response.data || [] }));
  yield put(actions.undo.success());
  yield put(getEventsList.request({}));
  yield call(report, t.EVENT_REMOVED, 'undo');
}

// The sessions it made go in the same grey cloud as an unplan; the ones a Replan or an unplan took off come back, as
// the server saved them, so there is no refetch and no empty moment in between.
function* undoPlanning(entry: UndoEntry, semesterId: number, planning: PlanningUndo) {
  const { ok, ...response } = yield apiCall(undoApi.undoPlanning, semesterId, planning, entry.actionId);
  if (!ok) return yield call(retryLater, entry, getErrorMessage(response));
  const removed: number[] = response.data?.deletedPlannedTaskIds || [];
  const restored: CalendarEvent[] = response.data?.restoredSessions || [];
  // Sessions the planning moved (a task dropped with Bumpers), back where they were, sliding there.
  const back: CalendarEvent[] = response.data?.movedSessions || [];
  // Everything marked used since, or back already.
  if (removed.length === 0 && restored.length === 0 && back.length === 0 && planning.reviewTaskIds.length === 0) {
    return yield call(changedSince, entry);
  }
  if (back.length > 0)
    followBlocks(
      back.map(event => event.id!),
      { sideways: false }
    );
  yield call(applyUnplanResult, response.data, true);
  if (back.length > 0) {
    const plannedEvents = yield select((state: RootState) => state.calendar.plannedEvents);
    yield put(
      applyTogether(back.map(newEvent => resizePlannedTask.success({ oldEvent: plannedEvents[newEvent.id!], newEvent })))
    );
  }
  yield call(clearTimerOn, timer => removed.includes(Number(timer.plannedTaskId)));
  if (planning.reviewTaskIds.length > 0) {
    yield put(deleteTasks.success({ ids: planning.reviewTaskIds, deleteForever: true }));
  }
  if (restored.length > 0) yield put(planTasks.success(restored));
  yield put(actions.undo.success());
  // Taking back an unplan only puts sessions back; anything else took back planning.
  const onlyRestored = planning.plannedSessionIds.length === 0 && planning.reviewTaskIds.length === 0;
  yield call(report, onlyRestored ? t.SESSIONS_RESTORED(restored.length) : t.PLANNING_UNDONE, 'undo');
}

// Puts a timer back on its session with the time it held, paused if asked. Another timer running since wins: starting
// this one would end it.
function* restoreTimer(
  entry: UndoEntry,
  semesterId: number,
  plannedTaskId: number,
  taskId: number | undefined,
  seconds: number,
  paused: boolean
) {
  const activeTimer = yield select(getActiveTimer);
  if (activeTimer && activeTimer.plannedTaskId !== plannedTaskId) return yield call(changedSince, entry);
  const started = yield apiCall(timerApi.start, semesterId, {
    uid: String(plannedTaskId),
    entryType: 'PLANNED_TASK',
    previousTimePassedInSeconds: seconds
  });
  if (!started.ok) return yield call(retryLater, entry, getErrorMessage(started));
  yield put(timerActions.start.success({ ...started.data, taskId }));
  if (paused) {
    const toggled = yield apiCall(timerApi.toggle, semesterId);
    if (toggled.ok) yield put(timerActions.toggle.success(toggled.data));
  }
  yield put(actions.undo.success());
  yield call(report, t.TIMER_BACK, 'undo');
}

// Reset cleared the whole session: put back its logged time, then the timer as it was.
function* undoTimerReset(entry: UndoEntry & { kind: 'timerReset' }, semesterId: number) {
  const { ok, ...response } = yield apiCall(plannerApi.updatePlannedTask, semesterId, entry.plannedTaskId, {
    used: entry.previousUsed
  });
  if (!ok) return yield call(retryLater, entry, getErrorMessage(response));
  yield put(updatePlannedTask.success({ data: response.data }));
  yield call(restoreTimer, entry, semesterId, entry.plannedTaskId, entry.taskId, entry.seconds, entry.paused);
}

function* undoSessionCleared(entry: UndoEntry & { kind: 'sessionCleared' }, semesterId: number) {
  const restored = yield apiCall(plannerApi.updatePlannedTask, semesterId, entry.plannedTaskId, { used: entry.used });
  if (!restored.ok) return yield call(retryLater, entry, getErrorMessage(restored));
  yield put(updatePlannedTask.success({ data: restored.data }));
  if (entry.isUsed) {
    const ticked = yield apiCall(plannerApi.markAsUsed, semesterId, entry.plannedTaskId, { isUsed: true } as any);
    if (!ticked.ok) return yield call(retryLater, entry, getErrorMessage(ticked));
    yield put(markAsUsed.success(ticked.data));
  }
  yield put(actions.undo.success());
  yield call(report, t.TIMER_BACK_LOGGED, 'undo');
}

/**
 * A ▶: the timer stops (the seconds it counted dropped) and everything goes back as it was, the pieces it split off
 * taken away, the sessions it took off put back, and the moved ones, its own block too, back where they were, sliding
 * there; a session that had time logged gets it back, and its tick. Another session's timer running since wins: there
 * is nothing of this left to undo.
 */
function* undoTimerStartRearranged(entry: UndoEntry & { kind: 'timerStartRearranged' }, semesterId: number) {
  const activeTimer = yield select(getActiveTimer);
  if (activeTimer && Number(activeTimer.plannedTaskId) !== entry.plannedTaskId) return yield call(changedSince, entry);
  // Ended here rather than through the timer's Reset, which would record an undo of its own. A retry after this went
  // through finds no timer and goes on.
  if (activeTimer) {
    const cancelled = yield apiCall(timerApi.cancel, semesterId);
    if (!cancelled.ok) return yield call(retryLater, entry, getErrorMessage(cancelled));
    const closing = closeTimerPill();
    if (closing > 0) yield delay(closing);
    yield put(timerActions.cancel.success({ activeTimer, response: cancelled.data }));
    if (cancelled.data?.resetSession?.plannedTaskDto) yield put(markAsUsed.success(cancelled.data.resetSession));
  }
  const { ok, ...response } = yield apiCall(undoApi.undoPlanning, semesterId, {
    plannedSessionIds: entry.createdIds,
    unplannedSessions: entry.removedSessions,
    reviewTaskIds: [],
    places: entry.places
  });
  if (!ok) return yield call(retryLater, entry, getErrorMessage(response));
  const back: CalendarEvent[] = response.data?.movedSessions || [];
  const restored: CalendarEvent[] = response.data?.restoredSessions || [];
  // They slide back to where they were, as they slid away: followed from here, as the changes below draw them.
  followBlocks(back.map(event => event.id!));
  yield call(applyUnplanResult, response.data, true);
  const plannedEvents = yield select((state: RootState) => state.calendar.plannedEvents);
  yield put(
    applyTogether([
      ...(restored.length > 0 ? [planTasks.success(restored)] : []),
      ...back.map(newEvent => resizePlannedTask.success({ oldEvent: plannedEvents[newEvent.id!], newEvent }))
    ])
  );
  // Stopping it cleared the session (the timer's Reset): its time and tick go back on once it is in its place, which
  // the server doesn't move a logged session to.
  if (entry.used) {
    const logged = yield apiCall(plannerApi.updatePlannedTask, semesterId, entry.plannedTaskId, { used: entry.used });
    if (!logged.ok) return yield call(retryLater, entry, getErrorMessage(logged));
    yield put(updatePlannedTask.success({ data: logged.data }));
  }
  if (entry.isUsed) {
    const ticked = yield apiCall(plannerApi.markAsUsed, semesterId, entry.plannedTaskId, { isUsed: true } as any);
    if (!ticked.ok) return yield call(retryLater, entry, getErrorMessage(ticked));
    yield put(markAsUsed.success(ticked.data));
  }
  yield put(actions.undo.success());
  yield call(report, t.TIMER_START_UNDONE, 'undo');
}

/**
 * A Bumpers drag, or Keep going's bump: the pieces it split off go, any it took off come back, and every session it
 * moved slides back where it was.
 */
function* undoSessionsBumped(entry: UndoEntry & { kind: 'sessionsBumped' }, semesterId: number) {
  const { ok, ...response } = yield apiCall(
    undoApi.undoPlanning,
    semesterId,
    {
      plannedSessionIds: entry.createdIds,
      unplannedSessions: entry.removedSessions || [],
      reviewTaskIds: [],
      places: entry.places
    },
    entry.actionId
  );
  if (!ok) return yield call(retryLater, entry, getErrorMessage(response));
  const back: CalendarEvent[] = response.data?.movedSessions || [];
  const restored: CalendarEvent[] = response.data?.restoredSessions || [];
  followBlocks(
    back.map(event => event.id!),
    { sideways: false }
  );
  yield call(applyUnplanResult, response.data, true);
  const plannedEvents = yield select((state: RootState) => state.calendar.plannedEvents);
  yield put(
    applyTogether([
      ...(restored.length > 0 ? [planTasks.success(restored)] : []),
      ...back.map(newEvent => resizePlannedTask.success({ oldEvent: plannedEvents[newEvent.id!], newEvent }))
    ])
  );
  yield put(actions.undo.success());
  yield call(report, t.SESSIONS_MOVED_BACK(entry.places.length), 'undo');
}

function* undoEntry(entry: UndoEntry, semesterId: number) {
  // Only what this change did: once something since has changed what it touched, it is refused rather than overwrite that.
  if (!(yield select(stillAsLeft, entry))) return yield call(changedSince, entry);
  if (entry.kind === 'tasks') {
    yield call(undoTasks, entry, semesterId, entry.receipt);
  } else if (entry.kind === 'event' || entry.kind === 'eventEdited') {
    yield call(undoEventChange, entry, semesterId, entry.before, entry.after);
  } else if (entry.kind === 'sessionMoved') {
    yield call(undoSessionMoved, entry, semesterId);
  } else if (entry.kind === 'sessionUsed') {
    yield call(undoSessionUsed, entry, semesterId);
  } else if (entry.kind === 'createdTasks') {
    yield call(undoCreatedTasks, entry, semesterId, entry.taskIds, entry.planned);
  } else if (entry.kind === 'planned') {
    yield call(undoPlanning, entry, semesterId, entry);
  } else if (entry.kind === 'timerReset') {
    yield call(undoTimerReset, entry, semesterId);
  } else if (entry.kind === 'sessionCleared') {
    yield call(undoSessionCleared, entry, semesterId);
  } else if (entry.kind === 'timerStartRearranged') {
    yield call(undoTimerStartRearranged, entry, semesterId);
  } else if (entry.kind === 'listOrder') {
    // Back between the tasks it stood between, sliding there like a drop; a move of its own, with no Undo of its own.
    const { taskId, after, before } = entry;
    yield put(moveTaskInList.request({ move: { taskId, after, before }, was: null }));
    yield put(actions.undo.success());
    yield call(report, t.TASK_MOVED_BACK, 'undo');
  } else if (entry.kind === 'sessionsBumped') {
    yield call(undoSessionsBumped, entry, semesterId);
  } else {
    yield call(undoCreatedEvent, entry, semesterId, entry.event);
  }
}

export function* undo({ payload: entry }: ReturnType<typeof actions.undo.request>) {
  if (taken.has(entry)) return;
  taken.add(entry);
  closeToast.get(entry)?.();
  closeToast.delete(entry);
  since = yield select(getSeq);
  const semesterId: number = yield select(semesterSelectors.getId);
  if (semesterOf.get(entry) !== semesterId) return;
  yield call(undoEntry, entry, semesterId);
  justUndone = true;
}

/**
 * Undos run one at a time, in the order asked, each to the end: each checks what the one before left, and a newer one
 * must not cancel an older one whose save is already on its way.
 */
function* undoInTurn() {
  const requests: Channel<ReturnType<typeof actions.undo.request>> = yield actionChannel(actions.undo.request);
  while (true) {
    const request: ReturnType<typeof actions.undo.request> = yield take(requests);
    try {
      yield call(undo, request);
    } catch (error) {
      yield put(actions.undo.failure(String(error)));
    }
  }
}

/**
 * Cmd+Z and the calendar's Undo button: the newest step, once the changes still on their way are in, so a drop undone
 * the moment after it isn't skipped for the change before it.
 */
export function* undoLatest() {
  yield call(writesSettled, WRITES_WAIT_MS);
  const entry: UndoEntry | null = yield select(getEntry);
  if (entry) {
    yield put(actions.undo.request(entry));
  } else if (justUndone) {
    // Run out because older changes fell off past the limit, or because there were no more.
    const dropped: boolean = yield select(getDropped);
    bottomToast({ message: dropped ? t.NOTHING_ELSE_TO_UNDO_MAX(UNDO_STEPS) : t.NOTHING_MORE_TO_UNDO, icon: 'info' });
  }
}

// What the history kept may be gone or changed under it: the calendar or the term reloaded, or the term's dates, time
// zone, awake times or holidays changed (which moves and remakes events). Logging out and a new term reset the store.
const WIPES = [clearCalendar, updateTerm.success, setAwakeTimes.success, updateSemesterHolidays.success];

function* forgetAll() {
  yield put(actions.clear());
}

export default function*(dispatch: Dispatch) {
  yield all([
    takeEvery(actions.record, showUndoToast, dispatch),
    takeEvery(actions.reinsert, offerAgain, dispatch),
    call(undoInTurn),
    takeEvery(actions.undoLatest, undoLatest),
    takeEvery(WIPES, forgetAll),
    takeEvery([clearState, actions.clear], closeBottomToast)
  ]);
}
